// Substitution Audit Log API
// Read audit entries (client-side) and write entries (server-side via service role key)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import type { SubstitutionAuditLogEntry, SubstitutionAuditAction } from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

// Service role key — only available in server-side / edge function contexts
const SUPABASE_SERVICE_ROLE_KEY = (import.meta as any).env
  ?.VITE_SUPABASE_SERVICE_ROLE_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

/**
 * Parameters for writing a new audit log entry.
 * Used by server-side helpers; client-side RLS blocks direct INSERT.
 */
export interface WriteAuditEntryParams {
  school_id: string
  absence_id?: string | null
  assignment_id?: string | null
  action: SubstitutionAuditAction
  performed_by?: string | null
  performed_by_name?: string | null
  target_date?: string | null
  details?: Record<string, unknown> | null
}

/**
 * Fetch all audit log entries for a given absence, ordered oldest-first.
 * Requires an authenticated session (RLS: SELECT allowed for admins).
 */
export async function getAuditLog(
  absenceId: string
): Promise<SubstitutionAuditLogEntry[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('absence_id', `eq.${absenceId}`)
    params.append('order', 'created_at.asc')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/substitution_audit_log?${params}`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch audit log')
    }

    return response.json()
  })
}

/**
 * Write a new entry to the substitution audit log.
 *
 * This is a server-side helper — it uses the service role key to bypass RLS,
 * since client-side INSERT is blocked by policy.
 *
 * If the service role key is not available (e.g. called from a browser context),
 * the function logs a warning and returns without throwing so that audit writes
 * never break the main application flow.
 */
export async function writeAuditEntry(entry: WriteAuditEntryParams): Promise<void> {
  if (!SUPABASE_SERVICE_ROLE_KEY) {
    console.warn(
      '[substitution-audit-api] SUPABASE_SERVICE_ROLE_KEY is not available — ' +
        'skipping audit log write. This is expected in browser contexts.'
    )
    return
  }

  const response = await fetch(`${SUPABASE_URL}/rest/v1/substitution_audit_log`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_SERVICE_ROLE_KEY,
      Authorization: `Bearer ${SUPABASE_SERVICE_ROLE_KEY}`,
      'Content-Type': 'application/json',
      Prefer: 'return=minimal',
    },
    body: JSON.stringify({
      school_id: entry.school_id,
      absence_id: entry.absence_id ?? null,
      assignment_id: entry.assignment_id ?? null,
      action: entry.action,
      performed_by: entry.performed_by ?? null,
      performed_by_name: entry.performed_by_name ?? null,
      target_date: entry.target_date ?? null,
      details: entry.details ?? null,
    }),
  })

  if (!response.ok) {
    // Audit failures should not crash the caller — log and continue
    let errorMessage = `HTTP ${response.status}`
    try {
      const error = await response.json()
      errorMessage = error.message || errorMessage
    } catch {
      // ignore JSON parse failure
    }
    console.error(`[substitution-audit-api] Failed to write audit entry: ${errorMessage}`)
  }
}
